import { useCallback, useEffect, useRef, useState } from "react";
import { LoaderUIContext } from "./loaderUiContext";
import { preloadGalleryFulls, preloadGalleryThumbs } from "./preloadSlideImages";
import { LoaderDogGame } from "./components/LoaderDogGame";
import { SlideDeck } from "./components/SlideDeck";

/** At least this long on first visit so the loader (and quips) are noticeable even when cache is hot. */
const MIN_LOADER_MS = 3200;

const LOADER_QUIPS = [
  "Sniffing out every photo…",
  "Good dogs load in parallel.",
  "Chasing pixels around the park…",
  "Wagging bits into the cache…",
  "Zoomies through the gallery…",
  "Hold your leashes — almost there…",
  "Treat incoming for every decode…",
];

export function App() {
  const [loading, setLoading] = useState(true);
  const [funLoader, setFunLoader] = useState(false);
  const [quipIndex, setQuipIndex] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /** First visit only: preload + minimum time, then dismiss. */
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const minTime = new Promise((resolve) => {
        setTimeout(resolve, MIN_LOADER_MS);
      });
      await Promise.all([preloadGalleryThumbs(), minTime]);
      if (!cancelled && mountedRef.current) setLoading(false);
      void preloadGalleryFulls();
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const stopLoader = useCallback(() => {
    setFunLoader(false);
    setLoading(false);
  }, []);

  /** From top bar: keep loader up (quips + dog) until Return — no auto timeout. */
  const replayLoader = useCallback(() => {
    setQuipIndex(0);
    setFunLoader(true);
    setLoading(true);
    void preloadGalleryThumbs();
    void preloadGalleryFulls();
  }, []);

  useEffect(() => {
    if (!loading) return;
    const id = setInterval(() => {
      setQuipIndex((i) => (i + 1) % LOADER_QUIPS.length);
    }, 1700);
    return () => clearInterval(id);
  }, [loading]);

  return (
    <LoaderUIContext.Provider value={{ loading, replayLoader }}>
      <div className={`app${loading ? " app--loading" : ""}`}>
        <SlideDeck />
        {loading ? (
          <div className="app-loader" role="status" aria-busy="true">
            <span className="visually-hidden">
              {funLoader
                ? "Fun loader is playing. Use Return to go back to the presentation."
                : "Loading presentation. Please wait."}
            </span>
            <div className="app-loader__stage">
              <LoaderDogGame
                variant={funLoader ? "game" : "animation"}
                onReturnToSlides={funLoader ? stopLoader : undefined}
              />
              <p className="app-loader__quip">{LOADER_QUIPS[quipIndex]}</p>
              <div className="app-loader__dots" aria-hidden="true">
                <span className="app-loader__dot" />
                <span className="app-loader__dot" />
                <span className="app-loader__dot" />
              </div>
              <p className="app-loader__tagline">
                {funLoader ? "Return when you are done — or play catch below" : "Photo fetch squad, reporting for duty"}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </LoaderUIContext.Provider>
  );
}
